import {ChipProps} from "@heroui/react";
import {SportType} from "./types";
import {
    currentRegistry,
    isRacquetSport as registryIsRacquetSport,
    ScoringType,
    sportDefinition,
    sportsForSurface,
    venueNounFor as registryVenueNounFor,
    liveRegistryMap,
} from "./sports/registry.ts";
import {SPORTS_LIST} from "./user/_components/useEntityEditor.tsx";

export const GameRound = {
    LeagueRound: 1,
    Semifinals:  2,
    Finals:      3,
    Completed:   4,
} as const;

export const GameType = {
    LeagueGame:     1,
    TournamentGame: 2,
    OneOffGame:     3,
} as const;

export const GameStatus = {
    Scheduled:          'scheduled',
    InProgress:         'in_progress',
    Completed:          'completed',
    Cancelled:          'cancelled',
    ChallengePending:   'challenge_pending',
    ChallengeAccepted:  'challenge_accepted',
    ChallengeDeclined:  'challenge_declined',
    ChallengeForfeited: 'challenge_forfeited',
    Bye:                'bye',
    Walkover:           'walkover',
    OrganizerOverride:  'organizer_override',
    DisputeResolved:    'dispute_resolved',
} as const;

export const AiInsightResultStatus = {
    Pending:    'pending',
    Processing: 'processing',
    Completed:  'completed',
    Failed:     'failed',
} as const satisfies Record<string, App.Enums.AiInsightResultStatus>;

export const UserRole = {
    Player:           1,
    Organizer:        2,
    Master:           3,
    EmptyAfterSignup: 4,
} as const;

export const UserReferralStatus = {
    Invited:   1,
    SignedUp:  2,
    Confirmed: 3,
} as const;

export const UserStatus = {
    Incomplete:        1,
    Active:            2,
    Injured:           3,
    Vacation:          4,
    Retired:           5,
    OnLeave:           6,
    InvitedByEmail:    7,
    Deleted:           8,
    ActiveNotPlayer:   9,
    ClaimedIncomplete: 10,
} as const;

export const ApplicationStatus = {
    Created:      1,
    Approved:     2,
    Rejected:     3,
    AutoRejected: 4,
    Invited:      5,
    Applied:      6,
    Removed:      7,
    Waitlisted:   8,
} as const;

export const BookingStatus = {
    Created:         1,
    Approved:        2,
    Blocked:         3,
    Cancelled:       4,
    AwaitingPayment: 5,
} as const;

export const LeagueStatus = {
    Incomplete:    1,
    SetupComplete: 2, // deprecated, events go to EntryOpen status
    EntryOpen:     3,
    EntryClosed:   4, // deprecated, closing entry goes to Live status
    Live:          5,
    Finished:      6,
} as const;

export const TournamentStatus = {
    Incomplete:    1,
    SetupComplete: 2, // deprecated, events go to EntryOpen status
    EntryOpen:     3,
    EntryClosed:   4, // deprecated, closing entry goes to Live status
    Live:          5,
    Finished:      6,
} as const;

export const LeagueType = {
    WinPercent: 1,
    Points:     2,
    Ladder:     3,
    Americano:  4,
    Season:     5,
    KingOfTheCourt: 6,
    Mexicano:   7,
} as const;

// ---- Sport lists ---------------------------------------------------------------------------
// Every sport map below derives from the backend sport registry (sports/registry.ts) and reads it
// on EVERY access. They used to be built once at import time, which on a first visit is before the
// live registry arrives: the flag-OFF snapshot was frozen in for the whole session, so a new sport
// had no scoring type and the create wizard asked it for a match type it never shows (Prompt 6).
// Components that must re-render when the registry lands subscribe with useSports().

/** Sport key → display label, from the registry's 'labels' surface (flag OFF: the twelve incl. other). */
export const SportTypes: Record<string, string> = liveRegistryMap(() => Object.fromEntries(
    sportsForSurface('labels').map((entry) => [entry.key, entry.label]),
));

// The racquet sports (note the historical 'pickelball' key). Racquet tournaments enter as
// Singles or Doubles pairs, never whole Teams — participants pickers use this to drop the option.
export const RACQUET_SPORTS: readonly string[] = currentRegistry().sports.filter((sport) => sport.racquet).map((sport) => sport.key);
export const isRacquetSport = (sport?: string | null): boolean => registryIsRacquetSport(sport);

/** Iconify icon per sport key, from every sport definition the registry carries. */
export const SportTypeIcons: Record<string, string> = liveRegistryMap(() => Object.fromEntries(
    currentRegistry().sports.map((sport) => [sport.key, sport.icon]),
));

/**
 * Example team name for the "add a team" placeholders, so the hint reads as a real club in the
 * sport being run. Sports with no entry keep the football default.
 */
export const SportTeamExample: Record<string, string> = {
    basketball: 'Chicago Bulls',
};

export function teamNameExample(sportType?: string | null): string {
    return (sportType && SportTeamExample[sportType]) || 'Manchester United';
}

/**
 * The playing-area noun for an event: the organizer's own choice (venue_term) first, else the
 * sport's area from the registry (Arena for esports, Pitch for the field sports, Court for the
 * rest), capitalised. Mirrors TournamentCourts::venueTerm on the backend so every screen agrees.
 */
export function venueNounFor(sport_type: string | null | undefined, venue_term?: string | null): string {
    return registryVenueNounFor(sport_type, venue_term);
}

/**
 * How each sport records a result, from the registry: 'sets' (set by set), 'score' (one final
 * score per side), 'series' (maps in a best-of), 'field' (a race or field result). Sports the
 * registry does not know are absent; callers default them to 'sets'.
 */
export const SportScoringType: Record<string, ScoringType> = liveRegistryMap(() => Object.fromEntries(
    currentRegistry().sports.map((sport) => [sport.key, sport.scoring_type]),
));

export const ParticipantsType = {
    DoublesIndividual: 1,
    DoublesTeam:       2,
    Singles:           3,
} as const;

// Match types offered for each participants type (Singles / Doubles / Teams). Shared by the
// tournament setup and the create wizard so the Match type always matches Participants.
export const MATCH_TYPES_BY_PARTICIPANTS: Record<number, string[]> = {
    [ParticipantsType.Singles]:           ['mens_singles', 'women_singles'],
    [ParticipantsType.DoublesIndividual]: ['mens_double', 'women_doubles', 'mixed_doubles'],
    [ParticipantsType.DoublesTeam]:       ['mens_teams', 'women_teams', 'mixed_teams'],
};

/* ---- Formats and participants per sport (Prompt 7) --------------------------------------------
   With multi-sport ON a sport offers only its definition's formats (its format group) and the
   entry shapes its `sides` allow. With the flag OFF every helper answers null and each surface
   keeps its own list, exactly as before. */

/** One key per format any create or setup screen offers. */
export type FormatKey = 'points' | 'season' | 'win_percent' | 'ladder'
    | 'round_robin' | 'groups_knockout' | 'knockout' | 'double_elimination' | 'swiss' | 'fight_card'
    | 'americano' | 'mexicano' | 'kotc' | 'rydercup' | 'clash'
    | 'leaderboard' | 'season_series';

/** The formats the sport offers with the flag ON, or null (flag OFF, or no sport yet): keep today's list. */
export function formatsFor(sport: string | null | undefined): Set<string> | null {
    const registry = currentRegistry();
    if (!registry.multi_sport || !sport) return null;
    const list = sportDefinition(sport, registry)?.format_list;
    return Array.isArray(list) ? new Set(list) : null;
}

/** Sides that enter as a named team (a Team with a name and a captain). */
export const TEAM_SIDES: readonly string[] = ['team', 'triples', 'fours'];

/**
 * The participants types a sport's `sides` allow, the default first, or null with the flag OFF.
 * singles and individual enter as Singles; team, triples and fours as a named team (DoublesTeam).
 * Doubles is pairs: in a league both rankings (individual, and fixed pairs) unless the sport also
 * enters named teams, where DoublesTeam means the team; in a tournament, Doubles.
 */
export function participantTypesFor(sport: string | null | undefined, surface: 'league' | 'tournament'): number[] | null {
    const registry = currentRegistry();
    if (!registry.multi_sport || !sport) return null;
    const definition = sportDefinition(sport, registry);
    if (!definition?.sides?.length) return null;
    const namedTeams = definition.sides.some((s) => TEAM_SIDES.includes(s));
    const out: number[] = [];
    for (const side of definition.sides) {
        if (side === 'singles' || side === 'individual') out.push(ParticipantsType.Singles);
        else if (TEAM_SIDES.includes(side)) out.push(ParticipantsType.DoublesTeam);
        else if (side === 'doubles') {
            out.push(ParticipantsType.DoublesIndividual);
            if (surface === 'league' && (definition.racquet || !namedTeams)) out.push(ParticipantsType.DoublesTeam);
        }
    }
    return [...new Set(out)];
}

/**
 * The quiz's formats for the sport. Flag ON: its definition's formats among the quiz's four. OFF,
 * exactly as before: all four, less Americano for a team-shaped sport.
 */
export function quizFormatKeys(sport: string | null | undefined, teamShaped: boolean): string[] {
    const all = ['round_robin', 'groups_knockout', 'knockout', 'americano'];
    const allowed = formatsFor(sport);
    return all.filter((f) => (allowed ? allowed.has(f) : !teamShaped || f !== 'americano'));
}

/** The sport's default entry shape, its first side, with the flag ON; null with it off (today's rules). */
export function defaultSide(sport: string | null | undefined): string | null {
    return currentRegistry().multi_sport && sport ? sportDefinition(sport)?.sides?.[0] ?? null : null;
}

/**
 * The sets_count a quick create sends. Flag ON, a non-racquet sport sends its definition's best-of
 * (esports best of 3, volleyball 5, a one-score sport 1). Racquet sports, and every sport with the
 * flag off, keep `fallback` (the 1 the quick creates always sent).
 */
export function eventSetsCount(sport: string | null | undefined, fallback = 1): number {
    const definition = currentRegistry().multi_sport && sport ? sportDefinition(sport) : undefined;
    return !definition || definition.racquet ? fallback : Math.max(1, Number((definition.defaults as any)?.best_of) || 1);
}

/**
 * What a named-team entry is called for the sport: "Teams", or pétanque's "Triples", lawn bowls'
 * "Triples or fours". Null when the sport has no named-team side (then DoublesTeam is a fixed pair).
 */
export function teamSideLabel(sport: string | null | undefined): string | null {
    const sides = sportDefinition(sport)?.sides || [];
    const shapes = sides.filter((s) => TEAM_SIDES.includes(s));
    if (!shapes.length) return null;
    if (shapes.includes('team')) return 'Teams';
    return shapes.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' or ').replace(/ or (\w)/, (_, c) => ` or ${c.toLowerCase()}`);
}

/**
 * Boxing groups its entrants by weight class and golf by flight: the same tournament divisions every
 * sport has, under the sport's own word. Everything else says Division.
 */
export function divisionNoun(sport: string | null | undefined, plural = false): string {
    const [one, many] = ({boxing: ['Weight class', 'Weight classes'], golf: ['Flight', 'Flights']} as Record<string, [string, string]>)[String(sport || '')] || ['Division', 'Divisions'];
    return plural ? many : one;
}

/** An example division name for the sport's own kind of division. */
export function divisionExample(sport: string | null | undefined): string {
    return ({boxing: 'Middleweight', golf: 'Flight A'} as Record<string, string>)[String(sport || '')] || 'Advanced';
}

/**
 * Whether handing out playing areas means anything for a tournament (mirrors the backend's
 * TournamentCourts::areaMode). 'areas' is today's behaviour, and the answer for every sport with the
 * flag OFF. Golf has none; chess none unless the organiser named a venue term; cricket on a single
 * ground plays one game after another there.
 */
export function playingAreaMode(tournament: {sport_type?: string | null; venue_term?: string | null; courts_available?: unknown} | null | undefined): 'areas' | 'single_ground' | 'none' {
    if (!currentRegistry().multi_sport || !tournament) return 'areas';
    const grounds = Array.isArray(tournament.courts_available) ? tournament.courts_available.filter(Boolean).length : 0;
    switch (String(tournament.sport_type || '')) {
        case 'golf':    return 'none';
        case 'chess':   return String(tournament.venue_term || '').trim() ? 'areas' : 'none';
        case 'cricket': return grounds > 1 ? 'areas' : 'single_ground';
        default:        return 'areas';
    }
}

export const DrawType = {
    RoundRobin:       1,
    Elimination:      2,
    Swiss:            3,
    DoubleElimination: 4,
} as const;

export const SubscriptionPlanType = {
    // Player subscription plans
    PlayerMonthly: 1,
    PlayerYearly:  2,

    // Club/Organizer subscription plans. Basic = new $19 "Starter" tier; Starter* = the $39 "Plus" tier.
    StarterMonthly: 10,
    StarterYearly:  11,
    ProMonthly:     12,
    ProYearly:      13,
    BasicMonthly:   14,
    BasicYearly:    15,
} as const;

export function SubscriptionTypeToAppleId(planType: typeof SubscriptionPlanType[keyof typeof SubscriptionPlanType]) {
    switch (planType) {
        case SubscriptionPlanType.PlayerMonthly:
            return 'player_monthly';
        case SubscriptionPlanType.PlayerYearly:
            return 'player_yearly';
        default:
            return null;
    }
}

export const PlayerSubscriptionTier = {
    Free: 100,
    Pro:  200,
} as const;

export type PlayerSubscriptionTierType = typeof PlayerSubscriptionTier[keyof typeof PlayerSubscriptionTier];

export const ClubSubscriptionTier = {
    Free:       100,
    Basic:      150,
    Starter:    200,
    Pro:        300,
    Enterprise: 400,
} as const;

export type ClubSubscriptionTierType = typeof ClubSubscriptionTier[keyof typeof ClubSubscriptionTier];

// Customer-facing plan names follow the Pay-as-you-go / Club / Pro model. The enum keys
// (Free/Starter/Pro/Enterprise) stay internal to avoid a churny rename of stored tier values.
export const ClubSubscriptionTierLabels: Record<ClubSubscriptionTierType, string> = {
    [ClubSubscriptionTier.Free]:       'Pay-as-you-go',
    [ClubSubscriptionTier.Basic]:      'Starter',
    [ClubSubscriptionTier.Starter]:    'Plus',
    [ClubSubscriptionTier.Pro]:        'Pro',
    [ClubSubscriptionTier.Enterprise]: 'Enterprise',
};

// Plan feature bullets shown in the upgrade UI — kept in sync with the marketing pricing page.
export const ClubPlanFeatures: Record<ClubSubscriptionTierType, string[]> = {
    [ClubSubscriptionTier.Free]: [
        'Unlimited tournaments & leagues',
        'Brackets & round robins',
        'Online registration & payments',
        'Live scores & rankings',
        'Court bookings',
        'Email support',
    ],
    // Basic enum = the $19 "Starter" tier.
    [ClubSubscriptionTier.Basic]: [
        'Unlimited players & teams on cash and free-entry events',
        'Record offline payments — cash, bank transfer, card at venue',
        'Unlimited active events at once',
        'Full season history, archives & player stats',
    ],
    // Starter enum = the $39 "Plus" tier.
    [ClubSubscriptionTier.Starter]: [
        'Reduced transaction rate',
        'No ads on your club & event pages',
        'Branded events & custom club page branding',
        'Sponsors & news on your club page',
        'Multiple organizers',
        'WhatsApp & email schedule notifications and reminders',
        'Club rewards',
        'Priority support',
    ],
    [ClubSubscriptionTier.Pro]: [
        'Lowest transaction rate',
        'Auto-scheduling & automatic waitlist promotion',
        'TV Mode for venue screens',
        'Gamified leaderboards',
        'Analytics dashboards',
        'Priority onboarding & dedicated account manager',
    ],
    [ClubSubscriptionTier.Enterprise]: [
        'Everything in Pro',
        'White-label',
        'Custom integrations',
        'Dedicated account manager',
    ],
};

export const ClubSubscriptionStatus = {
    Active:    'active',
    Cancelled: 'cancelled',
    PastDue:   'past_due',
    Trialing:  'trialing',
    Paused:    'paused',
} as const;

export type ClubSubscriptionStatusType = typeof ClubSubscriptionStatus[keyof typeof ClubSubscriptionStatus];

export const VisibilityType = {
    Private:       1,
    Unlisted:      2,
    Public:        3,
    ForcedPrivate: 4,
} as const;

export const VisibilityTypeStr = {
    Private: 'private',
    Public:  'public',
    Hidden:  'hidden',
} as const;

export const MessageType = {
    SystemNotification:   1,
    GameInvitation:       2,
    LeagueInvitation:     3,
    TournamentInvitation: 4,
    Invitation:           5,
    InvitationRejected:   6,

    BookingCreated:            7,
    BookingApproved:           8,
    BookingPlayerCancelled:    9,
    BookingOrganizerCancelled: 10,

    AssessmentCompleted: 11,

    InvitationApproved: 17,
} as const;

export const MessageRelatedType = {
    Game:       1,
    League:     2,
    Tournament: 3,
    Club:       4,
    Booking:    5,
    Assessment: 6,
} as const;

export const PostType = {
    Blog: 1,
    News: 2,
    Faq:  3,
} as const;

export const ClubType = {
    Venue:     1,
    Community: 2,
    // A competing team's own club page, created when an organizer invites that team's manager.
    // Mirrors App\Enums\ClubType. Never appears as a user's own club — User::club() and
    // User::community() filter to the two types above.
    Team:      3,
} as const;

export function VisibilityTypeText(visibility: typeof VisibilityType[keyof typeof VisibilityType]) {
    switch (visibility) {
        case VisibilityType.Private:
            return 'Private';
        case VisibilityType.Unlisted:
            return 'Unlisted';
        case VisibilityType.Public:
            return 'Public';
        case VisibilityType.ForcedPrivate:
            return 'Private (forced)';
    }
}

export function PostTypeText(visibility: typeof PostType[keyof typeof PostType]) {
    switch (visibility) {
        case PostType.Blog:
            return 'Blog Article';
        case PostType.News:
            return 'News';
        case PostType.Faq:
            return 'Faq Entry';
    }
}

export function GameRoundText(game_round: typeof GameRound[keyof typeof GameRound]) {
    switch (game_round) {
        case GameRound.LeagueRound:
            return 'League Round';
        case GameRound.Semifinals:
            return 'Semifinals';
        case GameRound.Finals:
            return 'Finals';
        case GameRound.Completed:
            return 'Completed';
    }
}

export function UserRoleText(user_role: typeof UserRole[keyof typeof UserRole]) {
    switch (user_role) {
        case UserRole.Player:
            return 'Player';
        case UserRole.Organizer:
            return 'Organizer';
        case UserRole.Master:
            return 'Master';
    }
}

export function ApplicationStatusToText(status: typeof ApplicationStatus[keyof typeof ApplicationStatus]) {
    switch (status) {
        case ApplicationStatus.Created:
            return 'Created';
        case ApplicationStatus.Approved:
            return 'Approved';
        case ApplicationStatus.Rejected:
            return 'Rejected';
        case ApplicationStatus.AutoRejected:
            return 'Rejected (Auto)';
        case ApplicationStatus.Invited:
            return 'Invited';
        case ApplicationStatus.Applied:
            return 'Applied';
        case ApplicationStatus.Removed:
            return 'Removed';
        case ApplicationStatus.Waitlisted:
            return 'Waitlisted';
    }
}

export function CoinsRewardText(reward: string) {
    switch (reward) {
        case 'amazon_usd25':
            return 'Amazon $25';
        case 'amazon_usd50':
            return 'Amazon $50';
        case 'amazon_usd100':
            return 'Amazon $100';
        case 'six_months_pro_access':
            return '6 Months Pro Access';
    }
    return reward;
}

export function BookingStatusToText(status: typeof BookingStatus[keyof typeof BookingStatus]) {
    switch (status) {
        case BookingStatus.Created:
            return 'Pending';
        case BookingStatus.Approved:
            return 'Approved';
        case BookingStatus.Blocked:
            return 'Blocked';
        case BookingStatus.Cancelled:
            return 'Cancelled';
        case BookingStatus.AwaitingPayment:
            return 'Awaiting payment';
    }
}

export function LeagueStatusText(status?: typeof LeagueStatus[keyof typeof LeagueStatus]) {
    switch (status) {
        case LeagueStatus.Incomplete:
            return 'Incomplete';
        case LeagueStatus.SetupComplete:
            return 'SetupComplete (deprecated)';
        case LeagueStatus.EntryOpen:
            return 'EntryOpen';
        case LeagueStatus.EntryClosed:
            return 'EntryClosed (deprecated)';
        case LeagueStatus.Live:
            return 'Live';
        case LeagueStatus.Finished:
            return 'Finished';
        default:
            return 'Unknown';
    }
}

// Derive a league's status from its date fields rather than the manual `status` workflow.
// Order matters: checked latest-phase first. A manual Finish (status === Finished) always wins.
// `cls` are Tailwind classes for the tile chip; callers can use `label` alone if they style themselves.
export type LeagueDateStatusKey = 'reg_upcoming' | 'reg_open' | 'reg_closed' | 'started' | 'finished';

export function leagueDateStatus(league: {
    status?: number;
    registration_phase?: string | null;
    entry_starts_at?: string | null;
    entry_ends_at?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
}): {key: LeagueDateStatusKey; label: string; cls: string} {
    const now = Date.now();
    const ms = (v?: string | null) => (v ? Date.parse(v) : null);
    const entryStart = ms(league.entry_starts_at);
    const entryEnd = ms(league.entry_ends_at);
    const start = ms(league.starts_at);
    const end = ms(league.ends_at);

    // Manually finished, or the event end date has passed.
    if (league.status === LeagueStatus.Finished || (end !== null && now >= end)) {
        return {key: 'finished', label: 'Event Finished', cls: 'bg-default-200 text-default-600'};
    }
    // LIVE beats the date logic: a recorded score flips the status server-side, and a league with
    // results is live regardless of what its (often unset) dates say — "Registration Open" over a
    // league mid-season was a lie.
    if (league.status === LeagueStatus.Live) {
        return {key: 'started', label: 'Live', cls: 'bg-green-100 text-green-700'};
    }
    // Event under way.
    if (start !== null && now >= start) {
        return {key: 'started', label: 'Event Started', cls: 'bg-green-100 text-green-700'};
    }
    // Registration window has closed, event not yet started.
    if (entryEnd !== null && now >= entryEnd) {
        return {key: 'reg_closed', label: 'Registration Closed', cls: 'bg-orange-100 text-orange-700'};
    }
    // Registration start in the future.
    if (entryStart !== null && now < entryStart) {
        return {key: 'reg_upcoming', label: 'Registration Opens Soon', cls: 'bg-amber-100 text-amber-700'};
    }
    // The public page's own answer (HasRegistrationPhase) where the league carries it.
    if (league.registration_phase) {
        if (['register', 'full', 'register_live'].includes(league.registration_phase)) {
            return {key: 'reg_open', label: 'Registration Open', cls: 'bg-blue-100 text-blue-700'};
        }
        if (league.registration_phase === 'closed') {
            return {key: 'reg_closed', label: 'Registration Closed', cls: 'bg-orange-100 text-orange-700'};
        }
        return {key: 'reg_upcoming', label: 'Registration Not Open', cls: 'bg-amber-100 text-amber-700'};
    }
    // Open once entries were opened, or the registration start has passed (registrationPhase()).
    // Without either it is not open — never "Registration Open" on a draft with no dates.
    if (league.status === LeagueStatus.EntryOpen || (entryStart !== null && now >= entryStart)) {
        return {key: 'reg_open', label: 'Registration Open', cls: 'bg-blue-100 text-blue-700'};
    }
    return {key: 'reg_upcoming', label: 'Registration Not Open', cls: 'bg-amber-100 text-amber-700'};
}

export function UserStatusText(status: typeof UserStatus[keyof typeof UserStatus]) {
    switch (status) {
        case UserStatus.Incomplete:
            return 'Incomplete';
        case UserStatus.Active:
            return 'Active';
        case UserStatus.Injured:
            return 'Injured';
        case UserStatus.Vacation:
            return 'Vacation';
        case UserStatus.Retired:
            return 'Retired';
        case UserStatus.OnLeave:
            return 'OnLeave';
        case UserStatus.InvitedByEmail:
            return 'Invited';
        case UserStatus.ClaimedIncomplete:
            return 'Claimed (Incomplete)';
    }
}

export function UserStatusToColor(status: typeof UserStatus[keyof typeof UserStatus]): ChipProps['color'] {
    switch (status) {
        case UserStatus.Incomplete:
            return 'default';
        case UserStatus.Active:
            return 'success';
        case UserStatus.Injured:
            return 'warning';
        case UserStatus.Vacation:
            return 'warning';
        case UserStatus.Retired:
            return 'warning';
        case UserStatus.OnLeave:
            return 'warning';
        case UserStatus.InvitedByEmail:
            return 'default';
        case UserStatus.ClaimedIncomplete:
            return 'warning';
    }
}

export function TournamentStatusText(status: typeof TournamentStatus[keyof typeof TournamentStatus]) {
    return LeagueStatusText(status);
}

export function MatchTypeText(matchType: string) {
    switch (matchType) {
        case 'mens_double':
            return 'Mens Doubles';
        case 'women_doubles':
            return 'Women Doubles';
        case 'mixed_doubles':
            return 'Mixed Doubles';
        case 'mens_singles':
            return 'Mens Singles';
        case 'women_singles':
            return 'Women Singles';
        case 'mens_teams':
            return 'Mens Teams';
        case 'women_teams':
            return 'Women Teams';
        case 'mixed_teams':
            return 'Mixed Teams';
    }
}

export function GradeToText(grade: string, sport = '') {
    const NON_PADEL_GRADES = {
        ['A']:        'Level 5 / Expert',
        ['B+']:       'Level 4.5 / Expert',
        ['B']:        'Level 4.5 / Expert',
        ['B-']:       'Level 4 / Advanced',
        ['C+']:       'Level 3.5 / High Intermediate',
        ['C']:        'Level 3 / Low Intermediate',
        ['C-']:       'Level 2.5 / Novice',
        ['D']:        'Level 2 / Newbie',
        ['Beginner']: 'Level 2 / Newbie',
    };

    // Pickleball uses DUPR-style ratings, not letter grades. The stored grade is unchanged;
    // only the displayed label differs. (Sport key is "pickelball" in SportTypes.)
    const PICKLEBALL_GRADES: Record<string, string> = {
        ['A']:        '5.0+',
        ['B+']:       '4.5',
        ['B']:        '4.0',
        ['B-']:       '3.5',
        ['C+']:       '3.0',
        ['C']:        '2.5',
        ['C-']:       '2.0',
        ['D']:        '1.5',
        ['Beginner']: '1.0',
    };

    if (sport === 'pickelball' || sport === 'pickleball') {
        // Pickleball labels are bare DUPR numbers (e.g. "3.5"); prefix so they read
        // as a level rather than an unlabelled number.
        return 'Level: ' + (PICKLEBALL_GRADES[grade] ?? grade);
    }

    // Padel now reads the same rich ladder as the other racquet sports ("C+ / Level 3.5 /
    // High Intermediate") instead of a bare "Grade C+". A custom free-typed level (e.g.
    // "Open Division", "U18 amateur") isn't a letter grade — show it exactly as entered.
    const label = (NON_PADEL_GRADES as Record<string, string>)[grade];
    return label ? `${grade} / ${label}` : grade;
}

export function SportToText(sport: SportType) {
    return SPORTS_LIST[sport];
}

export function AbilityTitle(ability: string) {
    switch (ability) {
        case 'PadelServe':
            return 'Serve';
        case 'PadelForehand':
            return 'Forehand';
        case 'PadelBackhand':
            return 'Backhand';
        case 'PadelLob':
            return 'Lob';
        case 'PadelVolley':
            return 'Volley';
        case 'PadelBandeja':
            return 'Bandeja';
        case 'PadelBajada':
            return 'Bajada';
        case 'PadelVibora':
            return 'Vibora';
        case 'PadelChiquita':
            return 'Chiquita';
        case 'PadelSmash':
            return 'Smash';
        case 'PadelRulo':
            return 'Rulo';
        case 'PadelDropShot':
            return 'Drop';
        case 'PadelGlass':
            return 'Glass';
    }
    return ability.replace(/[A-Z]/g, function (char) {
        return ' ' + char;
    }).trim();
}

export function AbilityDescription(ability: string) {
    switch (ability) {
        case 'PadelServe':
            return 'Power and accuracy of your serve';
        case 'PadelForehand':
            return 'Power, control, and reliability on the dominant side';
        case 'PadelBackhand':
            return 'Technique and consistency on the non-dominant side';
        case 'PadelLob':
            return 'Height, depth, and strategic use';
        case 'PadelVolley':
            return 'Net play, reflexes, and positioning';
        case 'PadelBandeja':
            return 'Controlled overhead with spin, used to reset play';
        case 'PadelBajada':
            return 'Aggressive shot after glass bounce';
        case 'PadelVibora':
            return 'Sliced overhead attack with side spin';
        case 'PadelChiquita':
            return 'Soft low shot to the opponent’s feet';
        case 'PadelSmash':
            return 'Powerful overhead to finish the point';
        case 'PadelRulo':
            return 'Topspin smash to force the ball out of the glass';
        case 'PadelDropShot':
            return 'Short, delicate shot near the net';
        case 'PadelGlass':
            return 'Control and play after glass rebound';
    }
    return '';
}
